import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  Filter,
  Download,
  Pencil,
  ArrowLeftCircle,
  Clock,
  HelpCircle,
  ArrowLeftRight,
  X,
  Trash,
  Star,
  Loader,
} from "lucide-react";
import userprofile from "../../assets/image/user.png";
import { useAppState } from "../../hooks/useAppState";
import refund from "../../assets/icons/Refund.svg";
import { updateActivityReview } from "../../services/activityService";
import tooltipIcon from "../../assets/icons/ActivityInfo.svg";
// Custom tooltip component
const Tooltip = ({ text, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {children}
      </div>
      {showTooltip && (
        <div className="absolute z-50 w-64 p-2 mt-2 text-sm text-[#696969] bg-[#FAFAFA] rounded-[8px] shadow-lg -left-28 top-full">
          {text}
        </div>
      )}
    </div>
  );
};

// Define props interface for the component
interface ViewModelProps {
  isOpen: boolean;
  onClose: () => void;
  activityTitle?: string;
  expertName?: string;
  expertEmail?: string;
  expertRating?: number;
  expertRatingUser: number;
  expertReviewCount?: number;
  customerName?: string;
  customerEmail?: string;
  customerRating?: number;
  customerRatingUser?: number;
  customerReviewCount?: number;
  activityType?: string;
  activityTime?: string;
  activityDuration?: string;
  activityCost?: string;
  activitySavings?: string;
  initialNotes?: {
    id: number;
    text: string;
    timestamp: string;
  }[];
  onEditActivity?: (data: any) => void; // Add this prop for edit functionality
  activityData?: any; // Add this prop for activity data
  expertOrgName?: string; // Add expert org name prop
  customerOrgName?: string; // Add customer org name prop
  expertTeamName?: string; // Add expert team name prop
  expertteamRole?: string; // Add expert team role prop
  customerTeamName?: string; // Add customer team name prop
  customerTeamRole?: string; // Add customer team role prop
}

// Interface for activity data and review editing
interface ActivityData {
  id?: string;
  expertRating?: number;
  expertRatingUser?: number;
  expertReview?: string;
  customerRating?: number;
  customerRatingUser?: number;
  customerReview?: string;
  expertName?: string;
  customerName?: string;
  role?: string;
  date?: string;
  startTime?: string;
  duration?: string;
  earnings?: string;
  editingExpertReview?: boolean;
  editingCustomerReview?: boolean;
  activityData?: any; // Add this prop for activity data
}

const ViewModel: React.FC<ViewModelProps> = ({
  isOpen = true,
  onClose = () => {},
  activityTitle = "",
  expertName = "",

  expertEmail = "",
  expertRating = 0,
  expertRatingUser = 0,
  expertReviewCount = 0,
  customerName = "",
  customerEmail = "",
  customerRating = 0,
  customerRatingUser = 0,
  customerReviewCount = 0,
  activityType = "",
  activityTime = "",
  activityDuration = "",
  activityCost = "0",
  activitySavings = "0",
  initialNotes = [],
  activityData = {},
  onEditActivity = () => {}, // Default empty function
  expertOrgName = "",
  customerOrgName = "",
  expertTeamName = "",
  expertteamRole = "",
  customerTeamName = "",
  customerTeamRole = "",
}) => {
  const [notes, setNotes] = useState(initialNotes);
  const [newNote, setNewNote] = useState("");
  const [showNotesSection, setShowNotesSection] = useState(false);
  const [notesMaxHeight, setNotesMaxHeight] = useState("");
  const [visibleNotes, setVisibleNotes] = useState(2); // Track number of visible notes
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isDeletingNote, setIsDeletingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [loading, setLoading] = useState(false);

  function formatRole(role) {
    switch (role) {
      case "customer_owner":
        return "Team Owner";
      case "expert_owner":
        return "Team Owner";
      case "customer_team_lead":
        return "Team Lead";
      case "expert_team_lead":
        return "Team Lead";
      case "customer_team_member":
        return "Team Member";
      case "expert_team_member":
        return "Team Member";
      default:
        return "Unknown Role";
    }
  }
  // Handle starting note edit
  const handleEditNoteStart = (note: {
    id: number;
    text: string;
    timestamp: string;
  }) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text);
    setIsEditingNote(true);
  };

  const getInitialsImage = (name: string) => {
    if (!name) return null;
    const initials = name
      .split(" ")
      .filter((part) => part.length > 0)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
    return (
      <div className="w-[65px] h-[65px] rounded-[13px] object-cover overflow-hidden bg-[#F15D18] flex items-center justify-center">
        <span className="text-white font-bold text-xl nunito-bold">
          {initials}
        </span>
      </div>
    );
  };
  // Handle canceling note edit
  const handleEditNoteCancel = () => {
    setEditingNoteId(null);
    setEditingNoteText("");
    setIsEditingNote(false);
  };

  // Handle saving edited note
  const handleEditNoteSave = async () => {
    if (!editingNoteText.trim() || editingNoteId === null) return;

    setIsSubmittingNote(true);

    try {
      // Get activity ID
      let activityId = getActivityId();

      // If no ID found through getActivityId, check directly in activityData
      if (!activityId) {
        if (activityData?.id) {
          activityId = activityData.id;
        } else if (activityData?.activityId) {
          activityId = activityData.activityId;
        } else if (
          typeof activityTitle === "string" &&
          activityTitle.trim() !== ""
        ) {
          activityId = activityTitle.trim();
        }
      }

      if (!activityId) {
        console.error("Activity ID extraction failed for note edit:", {
          activityTitle,
          activityData: activityData
            ? { id: activityData.id, activityId: activityData.activityId }
            : null,
        });
        throw new Error("Activity ID not found");
      }

      // Update the note in local state
      const updatedNotes = notes.map((note) =>
        note.id === editingNoteId ? { ...note, text: editingNoteText } : note
      );

      setNotes(updatedNotes);

      // Import the activity service
      const { updateActivityNotes } = await import(
        "../../services/activityService"
      );

      // Update the note in Firebase
      const result = await updateActivityNotes(activityId, updatedNotes);

      if (!result.success) {
        throw new Error(result.error);
      }

      // Call parent's handler with updated data for UI update
      onEditActivity({
        ...activityData,
        adminNotes: updatedNotes,
      });

      console.log("Admin note updated successfully");
    } catch (error) {
      console.error("Error updating admin note:", error);
      alert("Failed to update note. Please try again later.");
    } finally {
      setIsSubmittingNote(false);
      setEditingNoteId(null);
      setEditingNoteText("");
      setIsEditingNote(false);
    }
  };

  // Handle deleting a note
  const handleDeleteNote = async (noteId: number) => {
    if (window.confirm(" Are you sure you want to delete this note?")) {
      setIsDeletingNote(true);

      try {
        // Get activity ID
        let activityId = getActivityId();

        // If no ID found through getActivityId, check directly in activityData
        if (!activityId) {
          if (activityData?.id) {
            activityId = activityData.id;
          } else if (activityData?.activityId) {
            activityId = activityData.activityId;
          } else if (
            typeof activityTitle === "string" &&
            activityTitle.trim() !== ""
          ) {
            activityId = activityTitle.trim();
          }
        }

        if (!activityId) {
          console.error("Activity ID extraction failed for note deletion:", {
            activityTitle,
            activityData: activityData
              ? { id: activityData.id, activityId: activityData.activityId }
              : null,
          });
          throw new Error("Activity ID not found");
        }

        // Remove the note from local state
        const updatedNotes = notes.filter((note) => note.id !== noteId);
        setNotes(updatedNotes);

        // Import the activity service
        const { updateActivityNotes } = await import(
          "../../services/activityService"
        );

        // Update notes in Firebase (effectively removing the deleted note)
        const result = await updateActivityNotes(activityId, updatedNotes);

        if (!result.success) {
          throw new Error(result.error);
        }

        // Call parent's handler with updated data for UI update
        onEditActivity({
          ...activityData,
          adminNotes: updatedNotes,
        });

        console.log("Admin note deleted successfully");
      } catch (error) {
        console.error("Error deleting admin note:", error);
        alert("Failed to delete note. Please try again later.");
      } finally {
        setIsDeletingNote(false);
      }
    }
  };
  const [editActivityData, setEditActivityData] = useState<ActivityData>({
    expertRating: expertRating || 0,
    expertReview: activityData?.expertReview || "",
    customerRating: customerRating || 0,
    customerReview: activityData?.customerReview || "",
    editingExpertReview: false,
    editingCustomerReview: false,
    id: activityTitle,
  });
  console.log(activityTitle, "activityTitle");
  const { user } = useAppState();
  const [activityView, setActivityView] = useState(true);
  const [activityEditModel, setActivityEditModel] = useState(false);
  const modalRef = useRef(null);
  // Extract activity ID from title
  const getActivityId = () => {
    // First try to extract ID from pattern like "Activity (#123)"
    const match = activityTitle?.match(/\(#(\d+)\)/);
    if (match && match[1]) {
      return match[1];
    }

    // If not found, check if activityTitle itself is the ID
    if (activityTitle && !activityTitle.includes("(#")) {
      return activityTitle;
    }

    // If activityData has an ID, use that
    if (activityData && (activityData.id || activityData.activityId)) {
      return activityData.id || activityData.activityId;
    }

    return "";
  };
  console.log(customerReviewCount, "customerReviewCount"); // Handle opening the edit modal for expert review
  const handleOpenExpertEditModal = () => {
    const data = {
      id: getActivityId(),
      expertRating: expertRating || 0,
      expertReview: activityData?.expertReview || "",
      customerRating: customerRating || 0,
      customerReview: activityData?.customerReview || "",
      expertName,
      customerName,
      editingExpertReview: true,
      editingCustomerReview: false,
      activityTitle: activityTitle,
    };

    setEditActivityData(data);
    setActivityView(false);
    setActivityEditModel(true);
  };

  // Handle opening the edit modal for customer review
  const handleOpenCustomerEditModal = () => {
    const data = {
      id: getActivityId(),
      expertRating: expertRating || 0,
      expertReview: activityData?.expertReview || "",
      customerRating: customerRating || 0,
      customerReview: activityData?.customerReview || "",
      expertName,
      customerName,
      editingExpertReview: false,
      editingCustomerReview: true,
      activityTitle: activityTitle,
    };

    setEditActivityData(data);
    setActivityView(false);
    setActivityEditModel(true);
  };

  // Handle closing the edit modal
  const handleCloseEditModal = () => {
    setActivityEditModel(false);
    setActivityView(true);
  };

  // Handle expert rating change in the edit modal
  const handleExpertRatingChange = (star: number) => {
    setEditActivityData({
      ...editActivityData,
      expertRating: star,
    });
  };

  // Handle customer rating change in the edit modal
  const handleCustomerRatingChange = (star: number) => {
    setEditActivityData({
      ...editActivityData,
      customerRating: star,
    });
  };

  // Handle expert review text change in the edit modal
  const handleExpertReviewChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditActivityData({
      ...editActivityData,
      expertReview: e.target.value,
    });
  };

  // Handle customer review text change in the edit modal
  const handleCustomerReviewChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditActivityData({
      ...editActivityData,
      customerReview: e.target.value,
    });
  };
  // Handle saving the edited data
  const handleSaveActivityEdit = async () => {
    try {
      setLoading(true);
      // Try multiple methods to get the activity ID
      let activityId = getActivityId();

      // If no ID found through getActivityId, check directly in activityData
      if (!activityId) {
        if (activityData?.id) {
          activityId = activityData.id;
        } else if (activityData?.activityId) {
          activityId = activityData.activityId;
        } else if (
          typeof activityTitle === "string" &&
          activityTitle.trim() !== ""
        ) {
          // If activity title is not in the format (#123), use it directly
          activityId = activityTitle.trim();
        }
      }

      // If still no ID found, throw an error
      if (!activityId) {
        console.error("Activity ID extraction failed:", {
          activityTitle,
          activityData: activityData
            ? { id: activityData.id, activityId: activityData.activityId }
            : null,
        });
        throw new Error("Activity ID not found");
      }

      console.log("Using activityId:", activityId);

      // Prepare update data based on which review is being edited
      const updateData = editActivityData.editingExpertReview
        ? {
            expertRating: editActivityData.expertRating,
            expertReview: editActivityData.expertReview,
            updatedAt: new Date(),
          }
        : {
            customerRating: editActivityData.customerRating,
            customerReview: editActivityData.customerReview,
            updatedAt: new Date(),
          };

      // Update the activity through our service
      const result = await updateActivityReview(activityId, updateData);

      if (!result.success) {
        throw new Error(result.error);
      }

      // Create updated activity data to pass back to parent
      const updatedActivityData = {
        ...activityData,
        id: activityId,
        expertRating: editActivityData.editingExpertReview
          ? editActivityData.expertRating
          : activityData.expertRating,
        expertReview: editActivityData.editingExpertReview
          ? editActivityData.expertReview
          : activityData.expertReview,
        customerRating: editActivityData.editingCustomerReview
          ? editActivityData.customerRating
          : activityData.customerRating,
        customerReview: editActivityData.editingCustomerReview
          ? editActivityData.customerReview
          : activityData.customerReview,
      };

      // Call parent's handler with updated data for UI update
      onEditActivity(updatedActivityData);

      // Show success message
      alert("Your rating and review have been updated successfully!");
    } catch (error) {
      console.error("Error updating rating and review:", error);
      alert("Failed to update rating and review. Please try again later.");
    } finally {
      setActivityEditModel(false);
      setActivityView(true);
      setLoading(false);
    }
  };

  // Calculate appropriate height for notes section based on available screen space
  useEffect(() => {
    if (showNotesSection && modalRef.current) {
      const calculateMaxHeight = () => {
        const modalRect = modalRef.current.getBoundingClientRect();
        const modalTop = modalRect.top;
        const windowHeight = window.innerHeight;
        const availableSpace = windowHeight - modalTop - 250; // Accounting for padding and other elements
        const maxHeight = Math.max(200, availableSpace); // Minimum height of 200px
        setNotesMaxHeight(`${maxHeight}px`);
      };

      calculateMaxHeight();
      window.addEventListener("resize", calculateMaxHeight);

      return () => {
        window.removeEventListener("resize", calculateMaxHeight);
      };
    }
  }, [showNotesSection]); // Use effect to initialize notes from activityData if available
  useEffect(() => {
    if (activityData?.adminNotes && Array.isArray(activityData.adminNotes)) {
      // Convert admin notes from activityData to our format
      const formattedNotes = activityData.adminNotes.map((note, index) => ({
        id: note.id || index + 1,
        text: note.text || note.content || "",
        timestamp: note.timestamp || new Date().toLocaleString(),
      }));

      setNotes(formattedNotes);
      setVisibleNotes(formattedNotes.length); // Show all notes initially
    } else if (
      initialNotes &&
      Array.isArray(initialNotes) &&
      initialNotes.length > 0
    ) {
      // Use initialNotes if activityData doesn't have adminNotes
      setNotes(initialNotes);
      setVisibleNotes(initialNotes.length); // Show all notes initially
    } else {
      // Initialize with empty array if no notes
      setNotes([]);
      setVisibleNotes(0);
    }
  }, [activityData, initialNotes]);

  // Updated submit handler to save notes to Firebase
  const handleSubmit = async () => {
    if (!newNote.trim()) return;

    setIsSubmittingNote(true);

    try {
      const currentDate = new Date();
      const timestamp = currentDate.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }); // Create a unique ID using timestamp + random number
      const uniqueId = Date.now() + Math.floor(Math.random() * 1000);

      const newNoteObj = {
        id: uniqueId,
        text: newNote,
        timestamp: timestamp,
      }; // Update local state
      const updatedNotes = [...notes, newNoteObj];
      setNotes(updatedNotes);
      setNewNote("");
      setVisibleNotes(updatedNotes.length); // Show all notes when a new note is added

      // Try multiple methods to get the activity ID
      let activityId = getActivityId();

      // If no ID found through getActivityId, check directly in activityData
      if (!activityId) {
        if (activityData?.id) {
          activityId = activityData.id;
        } else if (activityData?.activityId) {
          activityId = activityData.activityId;
        } else if (
          typeof activityTitle === "string" &&
          activityTitle.trim() !== ""
        ) {
          // If activity title is not in the format (#123), use it directly
          activityId = activityTitle.trim();
        }
      }

      // If still no ID found, throw an error
      if (!activityId) {
        console.error("Activity ID extraction failed for notes:", {
          activityTitle,
          activityData: activityData
            ? { id: activityData.id, activityId: activityData.activityId }
            : null,
        });
        throw new Error("Activity ID not found");
      }

      console.log("Using activityId for notes:", activityId); // Import the activity service
      const { addNoteToActivity } = await import(
        "../../services/activityService"
      );

      // Update Firebase through our service
      const result = await addNoteToActivity(activityId, updatedNotes);
      if (!result.success) {
        // Revert local state if Firebase update failed
        setNotes(notes); // Revert to previous state
        setNewNote(newNote); // Restore the note text
        setVisibleNotes(notes.length); // Reset visible notes
        throw new Error(result.error || "Failed to save note to database");
      }

      // Call parent's handler with updated data for UI update
      onEditActivity({
        ...activityData,
        adminNotes: updatedNotes,
      });

      console.log("Admin note added successfully");
    } catch (error) {
      console.error("Error adding admin note:", error);
      // Show more specific error message
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to add note. Please try again later.";
      alert(errorMessage);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const toggleNotesSection = () => {
    // Don't allow hiding the section if notes is null
    if (notes === null) {
      setShowNotesSection(true);
    } else {
      setShowNotesSection(!showNotesSection);
    }
  };

  const handleLoadMore = () => {
    // Show two more notes on each "load more" click, up to the total number of notes
    setVisibleNotes((prev) => Math.min(prev + 2, notes.length));
  };

  const handleActivityClose = () => {
    onClose();
  };
  // Only show visible notes if notes array exists - reverse order to show newest first
  const displayedNotes = notes
    ? notes.slice().reverse().slice(0, visibleNotes)
    : [];

  // Determine if "load more" button should be shown
  const hasMoreNotes = notes && visibleNotes < notes.length;
  // Get date and time from activityTime
  const timeParts = activityTime ? activityTime.split(", ") : ["", ""];
  const activityDate = timeParts[0];
  const activityStartTime = timeParts[1];

  // Function to format time with AM/PM
  const formatTimeWithAMPM = (timeString: string) => {
    if (!timeString) return "07:27:01 PM";

    // If already contains AM/PM, return as is
    if (timeString.includes("AM") || timeString.includes("PM")) {
      return timeString;
    }

    // Parse 24-hour format and convert to 12-hour with AM/PM
    try {
      const [hours, minutes, seconds] = timeString.split(":").map(Number);
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      const formattedSeconds =
        seconds !== undefined ? `:${seconds.toString().padStart(2, "0")}` : "";
      return `${displayHours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}${formattedSeconds} ${ampm}`;
    } catch (error) {
      return timeString; // Return original if parsing fails
    }
  };

  if (!isOpen) return null;
  console.log(editActivityData, "editActivityData");
  return (
    <div ref={modalRef}>
      <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
        {activityView ? (
          <div
            className="rounded-[24px] shadow-lg bg-white p-4 w-[554px] mx-auto relative"
            style={{
              maxHeight: "700px",
              overflowY: "auto",
            }}
          >
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-[24px] nunito-semibold text-[#0A0A0A]">
                Activity{" "}
                <span className=" text-[20px] nunito-regular ">
                  (#{activityTitle})
                </span>
              </h2>
              <button
                className="p-1 rounded-md hover:bg-gray-100"
                onClick={handleActivityClose}
              >
                <X size={24} />
              </button>
            </div>{" "}
            <div className="flex gap-3 mb-4">
              <div className="flex-1 border border-[#D9D9D9] rounded-[16px] p-2 w-[233px]">
                <div className="flex gap-2">
                  <div className="flex flex-col items-center">
                    {activityData?.expertData?.profileImageUrl ? (
                      <img
                        src={activityData.expertData.profileImageUrl}
                        alt="Expert profile"
                        className="w-[65px] h-[65px] rounded-[13px] object-cover"
                        style={{
                          boxShadow:
                            "0px 2px 4px 0px rgba(24, 172, 241, 0.25), 0px -2px 4px 0px rgba(24, 172, 241, 0.25), 2px 0px 4px 0px rgba(24, 172, 241, 0.25), -2px 0px 4px 0px rgba(24, 172, 241, 0.25)",
                        }}
                      />
                    ) : (
                      getInitialsImage(expertName)
                    )}
                    <span className="text-[14px] text-[#404040] nunito-bold mt-1">
                      Expert
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <span className=" group relative">
                      <h3 className="nunito-semibold truncate text-[#0A0A0A] text-[16px]">
                        {expertName}
                      </h3>
                      {/* Tooltip on hover */}
                      <div className="absolute z-10 opacity-0 group-hover:opacity-100 top- bottom-6  px-2 py-1 bg-[#FAFAFA] text-[#696969] text-xs rounded-[8px] shadow-lg transition-opacity duration-200 pointer-events-none">
                        <div className="text-start">
                          <div className="nunito-regular text-[12px] text-[#696969] mb-1">
                            {expertName}
                          </div>
                        </div>
                      </div>
                    </span>
                    <div className="text-[#696969] nunito-medium text-[12px]">
                      <span className="truncate">{expertTeamName}</span>
                      <br />
                     <div className="w-full block truncate">
                      <span className="truncate">
                        {formatRole(expertteamRole)},{expertOrgName || ""}
                      </span>
                      </div>
<div className="group relative">

                      <div className="truncate">
                        <span className="text-[#696969] text-[10px] nunito-medium">
                          {expertEmail}
                        </span>
</div>
  {/* Tooltip on hover */}
                    <div className="absolute z-10 opacity-0 group-hover:opacity-100 top- bottom-5  px-2 py-1 bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg transition-opacity duration-200 pointer-events-none">  
                      <div className="text-start">
                        <div className="  mb-1">
                          {expertEmail}

                          
                        </div>
                      </div>
                    </div>
                      </div>
                    </div>{" "}
                    <div className="flex items-center mt-1">
                      <Star className="w-[14px] h-[14px] text-[#F5A038] fill-[#F5A038]" />
                      <p className="text-[10px] nunito-semibold text-[#919191] ml-1">
                        {expertRatingUser || 0} ({expertReviewCount} reviews)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <ArrowLeftRight className="text-[#696969] w-[24px] h-[24px]" />
              </div>
              <div className="flex-1 border border-[#D9D9D9] rounded-[16px] p-2 w-[233px]">
                <div className="flex gap-2">
                  <div className="flex flex-col items-center">
                    {activityData?.customerData?.profileImageUrl ? (
                      <img
                        src={activityData.customerData.profileImageUrl}
                        alt="Customer profile"
                        className="w-[65px] h-[65px] rounded-[13px] object-cover"
                        style={{
                          boxShadow:
                            "0px 2px 4px 0px rgba(241, 93, 24, 0.25), 0px -2px 4px 0px rgba(241, 93, 24, 0.25), 2px 0px 4px 0px rgba(241, 93, 24, 0.25), -2px 0px 4px 0px rgba(241, 93, 24, 0.25)",
                        }}
                      />
                    ) : (
                      getInitialsImage(customerName)
                    )}
                    <span className="text-[14px] text-[#404040] nunito-bold mt-1">
                      Customer
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className=" group relative">
                      <h3 className="nunito-semibold truncate text-[#0A0A0A] text-[16px]">
                        {customerName}
                      </h3>
                      {/* Tooltip on hover */}
                          <div className="absolute z-10 opacity-0 group-hover:opacity-100 top- right-0 bottom-5  px-2 py-1 bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg transition-opacity duration-200 pointer-events-none">
                  <div className="text-start">
                          <div className="nunito-regular text-[12px] text-[#696969] mb-1">
                            {customerName}
                          </div>
                        </div>
                      </div>
                    </span>
                    <div className="text-[#696969]  text-[12px] nunito-medium">
                      
                      <span className="truncate">{customerTeamName}</span>
                      <br />
                      <div className="w-full block truncate">
                      <span className="truncate">
                        {formatRole(customerTeamRole)},{customerOrgName || ""}
                      </span>
                    </div>
<div className="group relative">

                      <div className="truncate">
                        <span className="text-[#696969] text-[10px] nunito-medium">
                          {customerEmail}
                        </span>
</div>  {/* Tooltip on hover */}
                    <div className="absolute z-10 opacity-0 group-hover:opacity-100 top- right-0 bottom-5  px-2 py-1 bg-[#FAFAFA] nunito-regular text-[12px] text-[#696969] text-xs rounded-[8px] shadow-lg transition-opacity duration-200 pointer-events-none">
                      <div className="text-start">
                        <div className="  mb-1">
                          {customerEmail}

                          
                        </div>
                      </div>
                    </div>

                      </div>
                    </div>{" "}
                    <div className="flex items-center mt-1">
                      <Star className="w-[14px] h-[14px] text-[#F5A038] fill-[#F5A038]" />
                      <span className="text-[#919191] ml-1 text-[10px] nunito-semibold">
                        {customerRatingUser || 0} ({customerReviewCount}{" "}
                        reviews)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="border border-[#D9D9D9] p-3 rounded-[16px]">
              <div className="mb-6  ">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="nunito-semibold text-[16px] text-[#0A0A0A]">
                    Activity details
                  </h3>{" "}
                  <div className="flex items-center text-[#404040] text-sm">
                    <span className="mr-1 text-[#919191]">
                      <Clock className="w-[16px] h-[16px]" />{" "}
                    </span>
                    <span className="text-[#404040] text-[14px] nunito-regular">
                      {activityDate},{" "}
                      {formatTimeWithAMPM(activityStartTime) || "07:27:01 PM"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div>
                    <div className="text-[#404040] mb-1 text-[16px] nunito-medium">
                      Activity Type
                    </div>
                    <div className="text-[#F15D18] nunito-bold text-[16px]">
                      {activityType || "Video Call"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#404040] mb-1 text-[16px] nunito-medium">
                      Duration
                    </div>
                    <div className="text-[#F15D18] nunito-bold text-[16px]">
                      {activityDuration}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#404040] mb-1 text-[16px] nunito-medium">
                      {" "}
                      {user?.role == "Customer" ? "Cost" : "Earnings"}
                    </div>{" "}
                    <div className="flex items-center">
                      <span className="text-[#F15D18] nunito-bold mr-2 text-[16px]">
                        ${parseFloat(activityCost).toFixed(2)}
                      </span>
                      <span
                        className="flex bg-orange-100 text-[#F15D18] rounded-full px-2 py-1 text-[14px] relative group cursor-pointer"
                        title="Amount refunded"
                      >
                        <img src={refund} className="w-[20px] h-[20px] " />$
                        {activitySavings}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-[12px] text-[#696969] bg-[#FAFAFA] rounded-[8px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                          Amount refunded
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
              </div>{" "}
              <div className="mb-4">
                <div className="flex items-center mb-1">
                  <div className="nunito-semibold text-[16px] text-[#404040]">
                    Expert Rating (for customer)
                  </div>
                  {(user?.role === "Expert" ||
                    user?.role === "supporty_admin") && (
                    <Tooltip
                      text="Your feedback matters! 
                                       You have 48 hours after the session
                                       ends to rate and review. Then, once
                                        time's up, it's locked in!"
                    >
                      <img
                        className="ml-1 w-[16px] h-[16px] "
                        src={tooltipIcon}
                      />
                    </Tooltip>
                  )}
                </div>
                <div className="flex mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`text-3xl ${
                        star <= (expertRating || 0)
                          ? "text-orange-500"
                          : "text-gray-200"
                      }`}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
              <div className="mb-5">
                <div className="flex items-center mb-2">
                  <div className="nunito-semibold text-[16px] text-[#404040]">
                    Expert Review (for customer)
                  </div>
                  {(user?.role === "Expert" ||
                    user?.role === "supporty_admin") && (
                    <Tooltip
                      text="Your feedback matters! 
                                       You have 48 hours after the session
                                       ends to rate and review. Then, once
                                        time's up, it's locked in!"
                    >
                      <img
                        className="ml-1 w-[16px] h-[16px] "
                        src={tooltipIcon}
                      />
                    </Tooltip>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-[#919191] text-[16px] nunito-medium">
                    "{activityData?.expertReview || ""}"
                  </p>
                  {(user?.role === "Expert" ||
                    user?.role === "supporty_admin") && (
                    <button
                      className="text-[#696969] hover:text-gray-700"
                      onClick={handleOpenExpertEditModal}
                    >
                      <Pencil className="w-[24px] h-[24px]" />
                    </button>
                  )}
                </div>
                <div className="border-t my-4 border-[#D9D9D9]"></div>
              </div>
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <div className="nunito-semibold mb-1 text-[16px]  text-[#404040]">
                    Customer Rating (for expert)
                  </div>
                  {(user?.role === "Customer" ||
                    user?.role === "supporty_admin") && (
                    <Tooltip
                      text="Your feedback matters! 
                                       You have 48 hours after the session
                                       ends to rate and review. Then, once
                                        time's up, it's locked in!"
                    >
                      <img
                        className="ml-1 w-[16px] h-[16px] "
                        src={tooltipIcon}
                      />
                    </Tooltip>
                  )}
                </div>

                <div className="flex mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`text-3xl ${
                        star <= (customerRating || 0)
                          ? "text-orange-500"
                          : "text-gray-200"
                      }`}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center mb-2">
                  <div className="nunito-semibold mb-1 text-[16px] text-[#404040]">
                    Customer Review (for expert)
                  </div>
                  {(user?.role === "Customer" ||
                    user?.role === "supporty_admin") && (
                    <Tooltip
                      text="Your feedback matters! 
                                       You have 48 hours after the session
                                       ends to rate and review. Then, once
                                        time's up, it's locked in!"
                    >
                      <img
                        className="ml-1 w-[16px] h-[16px] "
                        src={tooltipIcon}
                      />
                    </Tooltip>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-[#919191] text-[16px] nunito-medium">
                    "{activityData?.customerReview || ""}"
                  </p>
                  {(user?.role === "Customer" ||
                    user?.role === "supporty_admin") && (
                    <button
                      className="text-[#696969] hover:text-gray-700"
                      onClick={handleOpenCustomerEditModal}
                    >
                      <Pencil className="w-[24px] h-[24px] text-[#696969]" />
                    </button>
                  )}
                </div>
              </div>
            </div>{" "}
            {/* Add Notes */}
            {user?.role === "supporty_admin" && (
              <div>
                <div
                  className=" mx-auto bg-white rounded-[16px] mt-4   border border-[#D9D9D9]"
                  style={{
                    maxHeight: notesMaxHeight,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {" "}
                  <div className="px-4 mt-3 border-gray-200 flex justify-between items-center">
                    <h2 className="text-[16px] nunito-semibold text-[#0A0A0A]">
                      Notes
                    </h2>{" "}
                    {/* Show button when there are no notes or when we want to allow toggling */}
                    <button
                      className="w-[71px] h-[22px] text-[#696969] text-sm underline"
                      onClick={toggleNotesSection}
                    >
                      {showNotesSection
                        ? notes === null || displayedNotes.length === 0
                          ? "Add Note"
                          : "Hide Note"
                        : "Add Note"}
                    </button>
                  </div>
                  {showNotesSection && (
                    <div className="px-3 mb-3 border-[#919191]">
                      <div className="relative border rounded-[16px] p-3">
                        <textarea
                          className="w-full p-3 border-b border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                          placeholder="Type here"
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          rows={1}
                        />
                        <div className="flex justify-end mt-1">
                          <button
                            className="px-4 py-2  h-[44px] text-[16px] bg-orange-500 text-white rounded-lg hover:bg-orange-600 focus:outline-none"
                            onClick={handleSubmit}
                            disabled={isSubmittingNote}
                          >
                            {isSubmittingNote ? "Submitting..." : "Submit"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="py-2" style={{ flexGrow: 1 }}>
                    {notes &&
                      displayedNotes.map((note) => (
                        <div
                          key={note.id}
                          className="px-4   border-gray-200 py-1"
                        >
                          <div className="flex items-center text-gray-500 text-sm mb-1">
                            <span className="mr-1">
                              <Clock className="w-[16px] h-[16px] text-[#404040]" />
                            </span>
                            <span className="text-[#404040] text-[14px] nunito-semibold">
                              {note.timestamp}{" "}
                            </span>
                            <div className="ml-auto flex space-x-4">
                              <button
                                className="text-[#919191] hover:text-gray-700"
                                onClick={() => handleEditNoteStart(note)}
                              >
                                <Pencil className="w-[20px] h-[20px]" />
                              </button>
                              <button
                                className="text-[#919191] hover:text-gray-700"
                                onClick={() => handleDeleteNote(note.id)}
                              >
                                <Trash className="w-[20px] h-[20px]" />
                              </button>
                            </div>
                          </div>
                          {isEditingNote && editingNoteId === note.id ? (
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={editingNoteText}
                                onChange={(e) =>
                                  setEditingNoteText(e.target.value)
                                }
                                disabled={isSubmittingNote}
                                className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                              />
                              <button
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                                onClick={handleEditNoteSave}
                                disabled={isSubmittingNote}
                              >
                                {isSubmittingNote ? "Saving..." : "Save"}
                              </button>
                              <button
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                onClick={handleEditNoteCancel}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <p className="text-[#919191] text-[16px] w-[370px] ">
                              {note.text}
                            </p>
                          )}
                        </div>
                      ))}
                    {hasMoreNotes && (
                      <div className="flex justify-center px-4 mt-2 mb-2 ">
                        <button
                          className="w-[490px] border rounded-[6px] h-[39px] hover:bg-gray-50"
                          onClick={handleLoadMore}
                        >
                          load more
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          activityEditModel && (
            <div className="bg-white p-2 rounded-[24px] shadow-[24px] w-[460px]">
              <div className="flex justify-between items-center px-2 pt-2 ">
                <h2 className="text-[24px] nunito-semibold text-[#0A0A0A]">
                  Edit - Activity{" "}
                  <span className="nunito-regular text-[20px]">
                    (#{activityTitle})
                  </span>
                </h2>
                <button
                  className="text-[#404040] hover:text-gray-700"
                  onClick={handleCloseEditModal}
                >
                  <X className="w-[32px] h-[32px]" />
                </button>
              </div>
              <div className="p-4 px-9  ">
                <div className="border border-[#C4C4C4] p-3 px-5 rounded-[16px]">
                  {editActivityData.editingExpertReview ? (
                    <>
                      <div className="mb-4">
                        <p className="text-[16px] nunito-semibold text-[#404040]">
                          Expert Rating (for customer)
                        </p>
                        <div className="flex space-x-1 mt-1">
                          {" "}
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              className={`text-3xl cursor-pointer ${
                                star <= (editActivityData.expertRating || 0)
                                  ? "text-[#F15D18]"
                                  : "text-[#EBEBEB]"
                              }`}
                              onClick={() => handleExpertRatingChange(star)}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="mb-2">
                        <p className="text-[16px] nunito-semibold text-[#404040]">
                          Expert Review (for customer)
                        </p>
                        <input
                          type="text"
                          value={editActivityData.expertReview}
                          onChange={handleExpertReviewChange}
                          disabled={loading}
                          className="w-full mt-1 p-2 border-[1px] text-[#404040] text-[14px] nunito-regular border-[#404040] rounded-[6px]"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-4">
                        <p className="text-[16px] nunito-semibold text-[#404040]">
                          Customer Rating (for expert)
                        </p>
                        <div className="flex space-x-1 mt-1">
                          {" "}
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              className={`text-3xl cursor-pointer ${
                                star <= (editActivityData.customerRating || 0)
                                  ? "text-[#F15D18]"
                                  : "text-[#EBEBEB]"
                              }`}
                              onClick={() => handleCustomerRatingChange(star)}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="mb-2">
                        <p className="text-[16px] nunito-semibold text-[#404040]">
                          Customer Review (for expert)
                        </p>
                        <input
                          type="text"
                          value={editActivityData.customerReview}
                          onChange={handleCustomerReviewChange}
                          disabled={loading}
                          className="w-full mt-1 p-2 border-[1px] text-[#404040] text-[14px] nunito-regular border-[#404040] rounded-[6px]"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="flex justify-center mb-2">
                <button
                  className="w-[120px] h-[40px] bg-[#F15D18] hover:bg-orange-600 text-white text-[16px] nunito-bold py-2 rounded-[8px]"
                  onClick={handleSaveActivityEdit}
                >
                  {loading ? <>Saving...</> : "Save"}
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ViewModel;
